"use client";

import type { Locale } from "@/lib/i18n/config";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { NativeAd } from "./native-ad";
import { RemoveAdsLink } from "./remove-ads-link";

/** The engine surface the post-apply unit draws from (see `AfterApplyInline`). */
export const AD_BREAK_SURFACE = "job_detail_after_apply";

const COPY = {
	en: {
		label: "Ad · 1 of 1",
		why: "Why this ad? You just applied — this is relevant to your next step.",
	},
	id: {
		label: "Iklan · 1 dari 1",
		why: "Kenapa iklan ini? Karena kamu baru melamar — iklan yang relevan untuk langkah berikutnya.",
	},
} as const;

/**
 * The in-flow "ad break" in the post-apply success panel (PR I, `ad_breaks`
 * arm of `ad-format-experiment`).
 *
 * Better Ads Standards shape every choice here: it is IN-FLOW, never a modal,
 * overlay, pop-up or countdown, so Chrome's ad filter has nothing to act on. It
 * takes space in the page and blocks nothing, and it is never a control the user
 * must pass. The one exit is "Remove ads — Pro" (`RemoveAdsLink`).
 *
 * It adds NO close button of its own. Whether the wrapped `NativeAd` shows one
 * is the payload's call (`adsDismissible`, false for unpaid users since #5911).
 *
 * The body is the existing `NativeAd` (one renderer, one set of impression and
 * click accounting). It renders nothing for Pro or a capped viewer, and when the
 * engine has no offer, or would fall back to the first-party house card, the
 * whole break collapses (`has-[…]:hidden`) so an "Ad · 1 of 1" header is never
 * left labelling an empty box.
 */
export function AdBreakCard({
	jobId,
	categoryId,
	locale,
}: {
	jobId: string;
	categoryId: string | null;
	locale: Locale;
}) {
	const guard = useAdGuardrails(AD_BREAK_SURFACE);
	// Undecided or blocked (Pro, session cap): no ad, so no label either.
	if (!guard.ready || guard.blocked) return null;

	const l = locale === "en" ? "en" : "id";
	const t = COPY[l];

	return (
		<section
			data-testid="ad-break-card"
			data-format="ad_break"
			aria-label={t.label}
			className="has-[[data-state=dismissed]]:hidden has-[[data-state=empty]]:hidden has-[[data-state=house]]:hidden"
		>
			<div className="mb-2 flex items-center justify-between gap-2">
				<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
					{t.label}
				</span>
				<RemoveAdsLink locale={l} surface={AD_BREAK_SURFACE} />
			</div>
			<NativeAd
				surface={AD_BREAK_SURFACE}
				jobId={jobId}
				categoryId={categoryId}
				locale={locale}
			/>
			<p className="mt-2 text-xs text-muted-foreground">{t.why}</p>
		</section>
	);
}
